import { ComponentProps } from "react";
import { IComponentParent } from "sonobello.utilities.react";

import { ProtectedAppContextProvider } from "../../AppContext";
import NoConfirmationNewPatientFlow, {
  PreQualifiedNoConfirmationNewPatientFlow
} from "../../Flows/Compositions/NoConfirmationNewPatientFlow";
import NoConfirmationNoBookFlow, {
  PreQualifiedNoConfirmationNoBookFlow
} from "../../Flows/Compositions/NoConfirmationNoBookFlow";
import NoConfirmationRebookFlow, {
  PreQualifiedNoConfirmationRebookFlow
} from "../../Flows/Compositions/NoConfirmationRebookFlow";
import SingleMedicalStepNewPatientFlow, {
  PreQualifiedSingleMedicalStepNewPatientFlow,
  PreQualifiedSingleMedicalStepNewPatientFlowResolver,
  SingleMedicalStepNewPatientFlowResolver
} from "../../Flows/Compositions/SingleMedicalStepNewPatientFlow";
import SingleMedicalStepNoBookFlow, {
  PreQualifiedSingleMedicalStepNoBookFlow,
  PreQualifiedSingleMedicalStepNoBookFlowResolver,
  SingleMedicalStepNoBookFlowResolver
} from "../../Flows/Compositions/SingleMedicalStepNoBookFlow";
import SingleMedicalStepRebookFlow, {
  PreQualifiedSingleMedicalStepRebookFlow,
  PreQualifiedSingleMedicalStepRebookFlowResolver,
  SingleMedicalStepRebookFlowResolver
} from "../../Flows/Compositions/SingleMedicalStepRebookFlow";
import FailoverFlowResolver from "../../Flows/types/FailoverFlowResolver";
import NameMatchFlowResolver from "../../Flows/types/NameMatchFlowResolver";
import PatientQualificationFlowResolver from "../../Flows/types/PatientQualificationFlowResolver";
import ExperimentVariantFlowResolver from "../../Flows/types/ExperimentVariantFlowResolver";
import ExperimentKey from "../../Experiment/types/ExperimentKey";
import EnvironmentConfiguration from "../../types/EnvironmentConfiguration";
import ExperimentAssignmentAppContextProvider, {
  IExperimentAssignmentAppContextProviderProps,
  CreatePatientExperimentHook
} from "../Components/ExperimentAssignmentAppContextProvider";
import useCreatePatientExperimentManualAuth from "../hooks/useCreatePatientExperiment";
import ExperimentContextValueProvider from "./ExperimentContextValueProvider";

const useCreatePatientExperimentDisabledIfExperimentForced: CreatePatientExperimentHook =
  EnvironmentConfiguration.forceExperimentalFlowVariant
    ? props => ({
        result: null,
        error: null,
        isLoading: false,
        execute: () => {
          // no-op: hook is disabled when forced variant is enabled
        }
      })
    : useCreatePatientExperimentManualAuth;

type FlowResolverFactory = (isPreQualified: boolean) => FailoverFlowResolver;

/**
 * @remarks
 * Resolver decision tree (highest priority first):
 * 1) Pre-qualified branch (when patient is pre-qualified):
 *    a) Directly match explicit treatment flow names (`PreQualifiedSingleMedicalStep*`) with no experiment lookup.
 *    b) For control-family names (`newPatient`/`rebook`/`noBook` and legacy aliases), evaluate the
 *       `SingleMedicalStepFlow` experiment and choose treatment vs control flow.
 *    c) Fallback to pre-qualified legacy control flows.
 * 2) Standard branch (all other patients):
 *    a) Directly match explicit treatment flow names (`SingleMedicalStep*`) with no experiment lookup.
 *    b) For control-family names (`newPatient`/`rebook`/`noBook` and legacy aliases), evaluate the
 *       `SingleMedicalStepFlow` experiment and choose treatment vs control flow.
 *    c) Fallback to legacy control flows.
 */
const buildStandardFlowResolver: FlowResolverFactory = isPreQualified =>
  new FailoverFlowResolver([
    new PatientQualificationFlowResolver(
      isPreQualified,
      new FailoverFlowResolver([
        PreQualifiedSingleMedicalStepNewPatientFlowResolver,
        PreQualifiedSingleMedicalStepRebookFlowResolver,
        PreQualifiedSingleMedicalStepNoBookFlowResolver,
        new NameMatchFlowResolver([
          new PreQualifiedNoConfirmationNewPatientFlow(),
          new PreQualifiedNoConfirmationRebookFlow(),
          new PreQualifiedNoConfirmationNoBookFlow()
        ])
      ])
    ),
    new FailoverFlowResolver([
      SingleMedicalStepNewPatientFlowResolver,
      SingleMedicalStepRebookFlowResolver,
      SingleMedicalStepNoBookFlowResolver,
      new NameMatchFlowResolver([
        new NoConfirmationNewPatientFlow(),
        new NoConfirmationRebookFlow(),
        new NoConfirmationNoBookFlow()
      ])
    ])
  ]);

const buildForcedVariantFlowResolver: FlowResolverFactory = isPreQualified =>
  new FailoverFlowResolver([
    new PatientQualificationFlowResolver(
      isPreQualified,
      new FailoverFlowResolver([
        new ExperimentVariantFlowResolver(
          new PreQualifiedSingleMedicalStepNewPatientFlow(),
          ExperimentKey.SingleMedicalStepFlow
        ),
        new ExperimentVariantFlowResolver(
          new PreQualifiedSingleMedicalStepRebookFlow(),
          ExperimentKey.SingleMedicalStepFlow
        ),
        new ExperimentVariantFlowResolver(
          new PreQualifiedSingleMedicalStepNoBookFlow(),
          ExperimentKey.SingleMedicalStepFlow
        )
      ])
    ),
    new FailoverFlowResolver([
      new ExperimentVariantFlowResolver(new SingleMedicalStepNewPatientFlow(), ExperimentKey.SingleMedicalStepFlow),
      new ExperimentVariantFlowResolver(new SingleMedicalStepRebookFlow(), ExperimentKey.SingleMedicalStepFlow),
      new ExperimentVariantFlowResolver(new SingleMedicalStepNoBookFlow(), ExperimentKey.SingleMedicalStepFlow)
    ])
  ]);

const buildFlowResolver: FlowResolverFactory = EnvironmentConfiguration.forceExperimentalFlowVariant
  ? buildForcedVariantFlowResolver
  : buildStandardFlowResolver;

const configuredExperimentAssignmentAppContextProvider: React.FC<
  IExperimentAssignmentAppContextProviderProps
> = props =>
  ExperimentAssignmentAppContextProvider({
    ...props,
    flowResolver: buildFlowResolver(
      props.session.patient.isPreviouslyQualified && !EnvironmentConfiguration.forcePatientUnqualified
    ),
    useCreatePatientExperiment: useCreatePatientExperimentDisabledIfExperimentForced,
    AppContext: ProtectedAppContextProvider
  });

const ExperimentContextAppContextProvider: React.FC<
  IComponentParent & Omit<IExperimentAssignmentAppContextProviderProps, "experimentContextProps">
> = ExperimentContextValueProvider<
  Pick<IExperimentAssignmentAppContextProviderProps, "experimentContextProps">,
  ComponentProps<typeof configuredExperimentAssignmentAppContextProvider>
>({
  Component: configuredExperimentAssignmentAppContextProvider,
  map: experimentContextProps => ({ experimentContextProps })
});

export default ExperimentContextAppContextProvider;
